import * as Api from '@ViewModels';
import { css, StyleDeclarationValue } from 'aphrodite';
import { inject, observer } from 'mobx-react';
import * as React from 'react';
import { toast } from 'sonner';
import {
	IContentCalendarSelectedSuggestion,
	IImpersonationContextComponentProps,
	IModalContext,
	ImpersonationContextKey,
	IMultiCampaignSchedulerResult,
	ModalChildComponentContextKey,
} from '../../../../../../models';
import { CampaignType } from '../../../../../../models/AdminModels';
import { useEnvironment, useErrorMessages, useUserSession } from '../../../../../../models/hooks/appStateHooks';
import { useEventLogging } from '../../../../../../models/Logging';
import { accountHasComplianceEnabled, getUniqueIdForSuggestion } from '../../../../../../models/UiUtils';
import { useAccountProfileQuery, useTemplateQueries } from '../../../../../../queries';
import { hasContentPreferences } from '../../../../../../utils/hasContentPreferences';
import { AiButton } from '../../../../../components-v2/AiButton';
import { Button } from '../../../../../components/Button';
import { useCampaignCalendarSuggestionsContext } from '../../../../../components/campaigns/CampaignCalendar/context';
import { ComplianceApprovalPrompt } from '../../../../../components/campaigns/ComplianceApprovalPrompt';
import { baseStyleSheet } from '../../../../../styles/styles';
import { useHandleAiGeneration } from '../../../hooks/handleAiGeneration';
import { WarningBanner, WarningBannerMessage } from '../../../presentation';
import { MultiSchedulerAiModal } from '../../aiModal';
import { MultiCampaignSchedulerLayout } from '../../MultiCampaignSchedulerLayout';
import { MultiBlogTemplateItem } from '../MultiBlogTemplateItem';
import { useScheduleBlogSuggestions } from './hooks';
import { styleSheet } from './styles';

interface IProps extends IImpersonationContextComponentProps, IModalContext {
	styles?: StyleDeclarationValue[];
}

type Author = {
	sendFrom: Api.ISendEmailFrom;
	sendFromUserId: string | null;
};

export const MultiBlogScheduler = inject(
	ImpersonationContextKey,
	ModalChildComponentContextKey
)(
	observer(function MultiBlogScheduler({ styles = [], impersonationContext, parentModal }: IProps) {
		const userSession = useUserSession();
		const errorMessages = useErrorMessages();
		const accountProfileQuery = useAccountProfileQuery({ impersonationContext });
		const { logApiError } = useEventLogging('MultiBlogScheduler');
		const requiresCompliance = accountHasComplianceEnabled(userSession, impersonationContext);

		const [generatedContent, setGeneratedContent] = React.useState<Record<string, Api.IRawRichTextContentState>>({});

		const {
			selectedSuggestions,
			deselectSuggestion,
			updateStartDateForSuggestion,
			getStartDateForSuggestion,
			onScheduleSuccess,
		} = useCampaignCalendarSuggestionsContext();

		const isAdminView = useEnvironment()?.appType === 'admin';

		const { pendingItems, queueItemForAiGeneration, isGenerating, processQueue, suggestionsWithErrors } =
			useHandleAiGeneration({
				contentType: 'blogTemplate',
				impersonationContext,
				onAiGenerationComplete: (suggestion: IContentCalendarSelectedSuggestion, toolCall: Api.BlogToolCall) => {
					const key = getUniqueIdForSuggestion(suggestion);
					setGeneratedContent(prev => ({ ...prev, [key]: toolCall.content }));
				},
			});
		const [showingOriginalContent, setShowingOriginalContent] = React.useState<string[]>([]);

		const [author, setAuthor] = React.useState<Author>(() => {
			let sendFrom: Api.ISendEmailFrom = Api.SendEmailFrom.CurrentUser;
			let sendFromUserId: string = null;
			if (impersonationContext?.isValid) {
				sendFrom = Api.SendEmailFrom.SelectedUser;
				sendFromUserId = impersonationContext.user?.id || null;
			} else {
				sendFromUserId = userSession.user.id;
			}

			return {
				sendFrom,
				sendFromUserId,
			};
		});

		const scheduledPostsRef = React.useRef<Map<string, Api.IBlogPost>>(new Map());
		const suggestionPostErrorsRef = React.useRef<
			Set<[IContentCalendarSelectedSuggestion, Api.IOperationResultNoValue]>
		>(new Set());

		const templateQueries = useTemplateQueries({
			impersonationContext: impersonationContext?.toJs(),
			templateIds: selectedSuggestions.map(t => t.templateReference.templateId),
			refetchOnWindowFocus: false,
			queryKeyProvider: (_, index) => {
				const suggestion = selectedSuggestions[index];
				return getUniqueIdForSuggestion(suggestion);
			},
		});

		const scheduleMutation = useScheduleBlogSuggestions({
			onError: error => {
				logApiError('ScheduleBlogSuggestions-Error', error);
				errorMessages.pushApiError(error);
			},
			onSuccess: (results, params) => {
				results.forEach((result, i) => {
					const suggestion = params.suggestions[i];
					if (result.status === 'fulfilled') {
						const blogPost = result.value;
						scheduledPostsRef.current.set(getUniqueIdForSuggestion(suggestion), blogPost);
					} else {
						const error = Api.asApiError(result.reason);
						const matchingSuggestion = selectedSuggestions.find(
							s => getUniqueIdForSuggestion(suggestion) === getUniqueIdForSuggestion(s)
						);
						suggestionPostErrorsRef.current.add([matchingSuggestion, error]);
					}
				});

				if (scheduledPostsRef.current.size === selectedSuggestions.length) {
					const scheduled = Array.from(scheduledPostsRef.current?.values()) || [];
					const scheduleResult: IMultiCampaignSchedulerResult = {
						scheduledSuggestions: selectedSuggestions,
						withCompliance: params.sendWithCompliance,
					};

					toast.success(`${scheduled.length} blog posts scheduled.`);

					const event = onScheduleSuccess(scheduleResult);
					if (!event.defaultPrevented) {
						parentModal.onRequestClose();
					}
				}
			},
		});

		const scheduleSuggestions = (sendWithCompliance = false, sendWithComplianceEmail?: string) => {
			suggestionPostErrorsRef.current?.clear();
			const unscheduledSuggestions = scheduledPostsRef.current
				? selectedSuggestions.filter(suggestion => {
						return !scheduledPostsRef.current.has(getUniqueIdForSuggestion(suggestion));
					})
				: selectedSuggestions;
			if (unscheduledSuggestions?.length) {
				scheduleMutation.mutate({
					impersonationContext,
					suggestions: unscheduledSuggestions.map(suggestion => {
						return {
							...suggestion,
							schedule: {
								startDate: getStartDateForSuggestion(suggestion).toISOString(),
							},
						} as IContentCalendarSelectedSuggestion;
					}),
					generatedContent,
					sendWithCompliance,
					sendWithComplianceEmail,
					forUserId: author.sendFromUserId,
					templateMap: Object.fromEntries(
						templateQueries.map(q => [
							q.data.id,
							{
								template: q.data,
								mainImage: q.data.attachments?.[0],
							},
						])
					),
				});
			}
		};

		const suggestionsWithoutDatesCount = selectedSuggestions.filter(s => !getStartDateForSuggestion(s)).length;

		const canSchedule =
			suggestionsWithoutDatesCount === 0 &&
			!scheduleMutation.isPending &&
			!isGenerating &&
			!templateQueries.some(q => q.isPending) &&
			(impersonationContext?.isValid ? impersonationContext.user?.id : true);

		const renderSideBar = () => {
			if (requiresCompliance) {
				return (
					<ComplianceApprovalPrompt
						disabled={!canSchedule}
						isLoading={scheduleMutation.isPending}
						campaignType={CampaignType.Blog}
						onScheduleCompliance={(e: React.MouseEvent<HTMLElement>, sendWithComplianceEmail: string) => {
							e.preventDefault();
							scheduleSuggestions(true, sendWithComplianceEmail);
						}}
						onScheduleWithoutCompliance={() => scheduleSuggestions()}
					/>
				);
			}

			return (
				<div>
					<Button
						disabled={!canSchedule || scheduleMutation.isPending}
						className={css(baseStyleSheet.horizontalStack, baseStyleSheet.fullWidth)}
						onClick={() => scheduleSuggestions()}
						label='Schedule'
						isLoading={scheduleMutation.isPending}
					/>
				</div>
			);
		};

		return (
			<div className={css(styleSheet.container, ...styles)}>
				{suggestionsWithoutDatesCount > 0 ? (
					<WarningBanner>
						<>
							<WarningBannerMessage>
								{`You have `}
								<strong>{suggestionsWithoutDatesCount}</strong>
								{` without a send date. Please select a date/time to continue scheduling these posts.`}
							</WarningBannerMessage>
						</>
					</WarningBanner>
				) : null}
				<MultiCampaignSchedulerLayout
					{...author}
					hideContactOwnerOption
					onSendFromChange={(option, user) => {
						impersonationContext?.updateUser(user?.id ? { id: user.id } : null);
						setAuthor({ sendFrom: option.sender.mode, sendFromUserId: user?.id });
					}}
					sideBar={renderSideBar()}
					title='Schedule Blog Posts'
					userSelectLabel='Author'
					styles={[styleSheet.schedulerContainer]}
				>
					{isAdminView && hasContentPreferences(accountProfileQuery.data) && (
						<div className={css(styleSheet.customizeAllContentContainer)}>
							<AiButton
								title='Make Content Unique with AI'
								onClick={() => {
									selectedSuggestions.forEach(suggestion => queueItemForAiGeneration(suggestion));
									processQueue();
								}}
								isLoading={isGenerating}
							/>
						</div>
					)}

					<div className={css(styleSheet.content)}>
						{templateQueries.map((templateQuery, i) => {
							const suggestion = selectedSuggestions[i];
							const id = getUniqueIdForSuggestion(suggestion);

							const generationFailed = suggestionsWithErrors.some(
								(s: IContentCalendarSelectedSuggestion) => id === getUniqueIdForSuggestion(s)
							);
							const shouldShowOriginalContent = showingOriginalContent.includes(id);
							const template = templateQuery.data;

							return (
								<MultiBlogTemplateItem
									onUpdateDate={newDate => updateStartDateForSuggestion(suggestion, newDate)}
									scheduled={scheduledPostsRef.current?.has(getUniqueIdForSuggestion(suggestion))}
									error={Array.from(suggestionPostErrorsRef.current || []).find(([s]) => s === suggestion)?.[1]}
									suggestion={suggestion}
									key={getUniqueIdForSuggestion(suggestion)}
									query={templateQuery}
									scheduleDate={getStartDateForSuggestion(suggestion)}
									onRemove={
										templateQueries.length > 1 && !scheduleMutation.isPending
											? () => deselectSuggestion(suggestion)
											: undefined
									}
									isGeneratingContent={pendingItems.some(item => item.templateReference.templateId === template.id)}
									generationFailed={generationFailed}
									onGenerateContent={() => {
										queueItemForAiGeneration(suggestion);
										processQueue();
									}}
									showOriginalContent={shouldShowOriginalContent}
									onShowOriginalContent={
										generatedContent[id]
											? () =>
													setShowingOriginalContent(prev => {
														if (prev.includes(id)) {
															return prev.filter(showingId => showingId !== id);
														}
														return [...prev, id];
													})
											: undefined
									}
									generatedContent={generatedContent[id]}
									{...author}
								/>
							);
						})}
					</div>

					<MultiSchedulerAiModal
						onGenerateContentClicked={() => {
							selectedSuggestions.forEach(suggestion => queueItemForAiGeneration(suggestion));
							processQueue();
						}}
						impersonationContext={impersonationContext}
					/>
				</MultiCampaignSchedulerLayout>
			</div>
		);
	})
);
